import type { Page } from '@inertiajs/core';
import { router } from '@inertiajs/vue3';
import { isLoginInertiaPage } from '@/mobile/native/inertiaPageCache';
import { showBundledRuntimeError } from '@/mobile/native/bundledErrorOverlay';

function normalizeBundledPage(page: Page): Page {
  let url = page.url || '/dashboard';

  try {
    url = new URL(url, 'https://app.local').pathname + new URL(url, 'https://app.local').search;
  } catch {
    // keep url as-is
  }

  if (url.startsWith('/api/mobile/settings/')) {
    url = url.replace('/api/mobile', '');
  } else if (url.startsWith('/api/mobile')) {
    url = '/dashboard';
  }

  return {
    ...page,
    url,
    version: '',
    clearHistory: false,
    encryptHistory: false,
  };
}

export type NavigateBundledOptions = {
  preserveScroll?: boolean;
  onSuccess?: () => void;
};

/**
 * Troca de tela no app bundled sem novo request Inertia (evita tela branca / 409).
 */
export function navigateBundledInertiaPage(
  page: Page,
  options: NavigateBundledOptions = {},
): Promise<void> {
  if (isLoginInertiaPage(page)) {
    return Promise.reject(new Error('Sessão inválida. Faça login novamente.'));
  }

  const normalized = normalizeBundledPage(page);

  document.getElementById('app')?.setAttribute('data-page', JSON.stringify(normalized));

  return new Promise((resolve, reject) => {
    let settled = false;

    const finish = () => {
      if (settled) {
        return;
      }

      settled = true;
      options.onSuccess?.();
      resolve();
    };

    const fail = (message: string) => {
      if (settled) {
        return;
      }

      settled = true;
      reject(new Error(message));
    };

    router.replace({
      component: normalized.component,
      url: normalized.url,
      props: normalized.props,
      version: '',
      clearHistory: normalized.clearHistory,
      encryptHistory: normalized.encryptHistory,
      preserveScroll: options.preserveScroll ?? true,
      onSuccess: finish,
      onFinish: finish,
      onError: (errors) => {
        const detail =
          typeof errors === 'object' && errors && 'message' in errors
            ? String((errors as { message?: string }).message)
            : 'Não foi possível abrir esta tela.';
        showBundledRuntimeError(detail);
        fail(detail);
      },
    });
  });
}
